import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, CartesianGrid
} from 'recharts';
import {
    X, Activity, Users, TrendingUp, TrendingDown,
    Mail, MessageSquare, Calendar, Clock, ChevronRight,
    Search, Filter, AlertTriangle, AlertCircle, Star, Check, Ghost, FileSignature
} from 'lucide-react';
import { useRelationshipsStore } from '../../store/relationshipsStore';
import { endpoints } from '../../api/api';
import PersonCard from '../relationships/PersonCard';
import GhostHunterView from '../ghosts/GhostHunterView';
import DraftApprovalView from '../email/DraftApprovalView';
import TaskListView from '../tasks/TaskListView';

const AnalyticsSidebar = ({ darkMode, onClose }) => {
    const [activeTab, setActiveTab] = useState('insights');
    const { relationships, fetchRelationships } = useRelationshipsStore();

    // Relationship Filters State
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Real data state
    const [insightsStats, setInsightsStats] = useState(null);
    const [atRiskContacts, setAtRiskContacts] = useState([]);
    const [insightsLoading, setInsightsLoading] = useState(true);

    // Fetch real insights on mount
    useEffect(() => {
        const fetchInsights = async () => {
            try {
                const [healthRes, atRiskRes] = await Promise.all([
                    endpoints.insights.getPatterns(),
                    endpoints.insights.getAtRisk()
                ]);
                setInsightsStats(healthRes.data);
                setAtRiskContacts(atRiskRes.data || []);
            } catch (err) {
                console.error('Failed to fetch insights:', err);
            } finally {
                setInsightsLoading(false);
            }
        };
        fetchInsights();
        fetchRelationships();
    }, [fetchRelationships]);

    // --- MOCK DATA (used as fallback if no real data) ---
    const communicationData = [
        { name: 'Mon', emails: 24, slack: 35 },
        { name: 'Tue', emails: 18, slack: 42 },
        { name: 'Wed', emails: 30, slack: 28 },
        { name: 'Thu', emails: 22, slack: 38 },
        { name: 'Fri', emails: 28, slack: 22 },
    ];

    const responseTimeData = [
        { hour: '9am', time: 15 },
        { hour: '12pm', time: 25 },
        { hour: '3pm', time: 8 },
        { hour: '5pm', time: 20 },
    ];

    const platformBreakdown = [
        { name: 'Email', value: 45, color: '#EA4335' },
        { name: 'Slack', value: 35, color: '#4A154B' },
        { name: 'Meet', value: 20, color: '#3B82F6' },
    ];

    // atRiskContacts is now loaded from API via useState, fallback mock:
    const displayAtRiskContacts = atRiskContacts.length > 0 ? atRiskContacts : [
        { name: 'Lisa Wang', score: 45, trend: 'down', lastContact: '1w ago' },
        { name: 'Mark Johnson', score: 52, trend: 'down', lastContact: '5d ago' },
    ];

    // --- HELPER COMPONENT ---
    const StatRow = ({ label, value, trend, icon: Icon, color }) => (
        <div className={`p-3 rounded-xl border mb-2 ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100'}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                        <Icon size={16} color={color} />
                    </div>
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{label}</span>
                </div>
                <div className="text-right">
                    <div className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{value}</div>
                    <div className={`text-xs flex items-center gap-0.5 ${trend > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {trend > 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {Math.abs(trend)}%
                    </div>
                </div>
            </div>
        </div>
    );

    // --- RELATIONSHIP FILTERING ---
    const getFilteredRelationships = () => {
        return relationships.filter(p => {
            const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.role.toLowerCase().includes(searchQuery.toLowerCase());

            let matchesStatus = true;
            if (filterStatus === 'vip') matchesStatus = p.isVip;
            if (filterStatus === 'at-risk') matchesStatus = p.score < 50;
            if (filterStatus === 'active') matchesStatus = p.score >= 70;

            return matchesSearch && matchesStatus;
        });
    };

    const displayRelationships = getFilteredRelationships();

    return (
        <div className={`h-full flex flex-col border-l shadow-2xl ${darkMode ? 'bg-[#0F172A] border-white/10' : 'bg-white border-slate-200'}`}>

            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between shrink-0 ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
                <div className="flex gap-2 p-1 rounded-lg bg-slate-100 dark:bg-white/5">
                    <button
                        onClick={() => setActiveTab('insights')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === 'insights'
                            ? 'bg-white dark:bg-white/10 shadow-sm text-slate-900 dark:text-white'
                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                            }`}
                    >
                        Insights
                    </button>
                    <button
                        onClick={() => setActiveTab('relationships')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === 'relationships'
                            ? 'bg-white dark:bg-white/10 shadow-sm text-slate-900 dark:text-white'
                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                            }`}
                    >
                        Relationships
                    </button>
                    <button
                        onClick={() => setActiveTab('ghosts')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-all ${activeTab === 'ghosts'
                            ? 'bg-white dark:bg-white/10 shadow-sm text-slate-900 dark:text-white'
                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                            }`}
                    >
                        <Ghost size={12} /> Ghosts
                    </button>
                </div>
                <button onClick={onClose} className={`p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors`}>
                    <X size={18} className={darkMode ? 'text-slate-400' : 'text-slate-500'} />
                </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                {activeTab === 'insights' ? (
                    <div className="space-y-6">
                        {/* Quick Stats - Real Data */}
                        <div className="space-y-1">
                            <StatRow icon={Users} label="Total Contacts" value={insightsStats?.total || 0} trend={0} color="#4285F4" />
                            <StatRow icon={TrendingUp} label="Strong" value={insightsStats?.strong || 0} trend={0} color="#10B981" />
                            <StatRow icon={AlertCircle} label="At Risk" value={insightsStats?.at_risk || 0} trend={0} color="#F59E0B" />
                        </div>

                        {/* Activity Chart */}
                        <div className={`p-4 rounded-xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100'}`}>
                            <h3 className={`text-xs font-semibold mb-4 uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Volume Trend
                            </h3>
                            <div className="h-40">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={communicationData}>
                                        <defs>
                                            <linearGradient id="colorEmailsSidebar" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: darkMode ? '#1e293b' : '#fff',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '12px'
                                            }}
                                        />
                                        <Area type="monotone" dataKey="emails" stroke="#3B82F6" strokeWidth={2} fillOpacity={1} fill="url(#colorEmailsSidebar)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* At Risk Section */}
                        <div className={`p-4 rounded-xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100'}`}>
                            <div className="flex items-center gap-2 mb-3">
                                <AlertTriangle size={14} className="text-red-500" />
                                <h3 className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Action Required
                                </h3>
                            </div>
                            <div className="space-y-2">
                                {displayAtRiskContacts.map((contact, idx) => (
                                    <div key={idx} className={`p-2 rounded-lg flex items-center justify-between ${darkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                                        <div>
                                            <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>{contact.name}</div>
                                            <div className="text-[10px] text-red-400">Score: {contact.score} • Last: {contact.lastContact}</div>
                                        </div>
                                        <ChevronRight size={14} className={darkMode ? 'text-slate-500' : 'text-slate-400'} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Platform Breakdown Pie */}
                        <div className={`p-4 rounded-xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100'}`}>
                            <h3 className={`text-xs font-semibold mb-2 uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Platform Mix
                            </h3>
                            <div className="h-32 flex items-center justify-between">
                                <ResponsiveContainer width="50%" height="100%">
                                    <PieChart>
                                        <Pie data={platformBreakdown} cx="50%" cy="50%" innerRadius={25} outerRadius={40} paddingAngle={5} dataKey="value">
                                            {platformBreakdown.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="space-y-1 w-1/2">
                                    {platformBreakdown.map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.color }} />
                                            <span className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>{item.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                ) : activeTab === 'ghosts' ? (
                    <GhostHunterView darkMode={darkMode} />
                ) : (
                    /* Relationships Tab */
                    <div className="space-y-4">
                        {/* Search & Filter */}
                        <div className="space-y-2">
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
                                <Search size={14} className={darkMode ? 'text-slate-500' : 'text-slate-400'} />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`flex-1 bg-transparent text-sm focus:outline-none ${darkMode ? 'placeholder-slate-500' : 'placeholder-slate-400'}`}
                                />
                                {searchQuery && <button onClick={() => setSearchQuery('')}><X size={14} /></button>}
                            </div>

                            <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                                {[
                                    { id: 'all', label: 'All' },
                                    { id: 'vip', label: 'VIP', icon: Star },
                                    { id: 'at-risk', label: 'Risk', icon: AlertTriangle },
                                    { id: 'active', label: 'Active', icon: Check }
                                ].map(f => (
                                    <button
                                        key={f.id}
                                        onClick={() => setFilterStatus(f.id)}
                                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium whitespace-nowrap border transition-all ${filterStatus === f.id
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : darkMode ? 'bg-white/5 border-transparent text-slate-400 hover:bg-white/10' : 'bg-slate-100 border-transparent text-slate-600 hover:bg-slate-200'
                                            }`}
                                    >
                                        {f.icon && <f.icon size={10} />}
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* List */}
                        <div className="space-y-3">
                            {displayRelationships.length === 0 ? (
                                <div className="text-center py-8 opacity-50">
                                    <Users size={24} className="mx-auto mb-2" />
                                    <p className="text-xs">No contacts found</p>
                                </div>
                            ) : (
                                displayRelationships.map((person, idx) => (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                        key={person.id}
                                    >
                                        <PersonCard
                                            person={person}
                                            darkMode={darkMode}
                                            compact={true}
                                            onClick={() => { }}
                                        />
                                    </motion.div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnalyticsSidebar;
